import React, { useMemo, useRef } from "react"
import { getMonth, getYear } from "date-fns"
import { ChevronLeft, ChevronRight } from "@walmart-web/livingdesign-icons"
import useShortMonths from "../hooks/useShortMonths"
import { CalendarNavigationProps } from "../MultiRangeCalendar.domain"
import { range } from "./utils"
import { MIN_YEAR_OFFSET, RANGE_STEP } from "../constants"

const defaultMaxYear = getYear(new Date())
const defaultMinYear = getYear(new Date()) - MIN_YEAR_OFFSET
const intialRange = range(defaultMinYear, defaultMaxYear, RANGE_STEP)

export const CalendarNavigation = (props: CalendarNavigationProps) => {
	const {
		monthsShown = 2,
		decreaseMonth,
		changeMonth,
		increaseMonth,
		changeYear,
		date,
	} = props

	const rangeYear = useRef(intialRange)
	const calculatedStyles = { width: `calc(100% * min(${monthsShown}, 3))` }
	const currentYear = getYear(date)

	const years = useMemo(() => {
		if (currentYear > defaultMaxYear) {
			rangeYear.current = range(rangeYear.current[0], currentYear, RANGE_STEP)
		} else if (currentYear < defaultMinYear) {
			const [lastYear = defaultMaxYear] = rangeYear.current.slice(-1)
			rangeYear.current = range(currentYear, lastYear, RANGE_STEP)
		}

		return rangeYear.current
	}, [currentYear, defaultMaxYear])

	const shortMonths = useShortMonths()

	const handleChangeYear = ({
		target: { value },
	}: React.ChangeEvent<HTMLSelectElement>) => {
		changeYear(Number(value))
	}

	const handleChangeMonth = ({
		target: { value },
	}: React.ChangeEvent<HTMLSelectElement>) => {
		changeMonth(shortMonths.indexOf(value))
	}

	return (
		<div className="multi-calendar-range__navigation" style={calculatedStyles}>
			<button
				className="multi-calendar-range__button-nav"
				onClick={decreaseMonth}
			>
				<ChevronLeft size="small" />
			</button>
			<div className="multi-calendar-range__controls">
				<select
					className="multi-calendar-range__select"
					value={shortMonths[getMonth(date)]}
					data-testid="month-select"
					onChange={handleChangeMonth}
				>
					{shortMonths.map(renderOption)}
				</select>
				<select
					className="multi-calendar-range__select"
					value={getYear(date)}
					data-testid="year-select"
					onChange={handleChangeYear}
				>
					{years.map(renderOption)}
				</select>
			</div>
			<button
				data-testid="calendar-nav-increase"
				className="multi-calendar-range__button-nav"
				onClick={increaseMonth}
			>
				<ChevronRight size="small" />
			</button>
		</div>
	)
}

const renderOption = (option: number | string) => (
	<option key={String(option)} value={option}>
		{option}
	</option>
)
